import type { RecordTablePayload } from '../RecordTable'
import type { ClientView, FundView } from '../navigation/context'
import { chainLevels, chainOf } from './chainTree'

/** The existing Overview rules, shared by the prose and Fund facts. */
export function overviewStructureFacts(table: RecordTablePayload) {
  const chain = chainOf(table.segments)
  const recordedIds = new Set(
    table.instruments.filter((item) => item.state !== 'proposed').map((item) => item.id)
  )
  const entityById = new Map(table.entities.map((entity) => [entity.id, entity]))
  const holderIds = new Set(chain.map((segment) => segment.holder_id))
  const order = ['private equity', 'real estate', 'private credit', 'funds of funds']
  const classes = new Set<string>()
  for (const segment of chain) {
    if (!segment.instrument_id || !recordedIds.has(segment.instrument_id) || !segment.issuer_id)
      continue
    const issuer = entityById.get(segment.issuer_id)
    const isFund = issuer?.entity_nature === 'collective_vehicle'
    const isTarget = !holderIds.has(segment.issuer_id)
    if (!isTarget && !isFund) continue
    if (segment.instrument_category === 'fictitious') classes.add('real estate')
    else if (segment.instrument_category === 'debt') classes.add('private credit')
    else if (segment.instrument_category === 'equity')
      classes.add(isFund ? 'funds of funds' : 'private equity')
  }
  return {
    classes: order.filter((value) => classes.has(value)),
    levels: chainLevels(chain),
    targets: new Set(
      chain
        .map((segment) => segment.issuer_id)
        .filter((id): id is string => !!id && !holderIds.has(id))
    ).size,
    instruments: new Set(chain.map((segment) => segment.instrument_id).filter(Boolean)).size,
  }
}

export type FundClientHint = FundView['client_hints'][number]
export interface FundClientContext {
  id: string
  name: string | null
  hints: FundClientHint[]
}

/** All supplied client identities, independent of the paged client catalogue. */
export function fundClientContexts(
  fundId: string,
  hints: readonly FundClientHint[],
  selectedClient?: ClientView | null
): FundClientContext[] {
  const current = (selectedClient?.fund_folders ?? [])
    .filter(
      (folder) =>
        folder.match_status !== 'unmatched' &&
        folder.matching_registered_funds.some((fund) => fund.id === fundId)
    )
    .map((folder): FundClientHint => ({
      client_id: selectedClient!.id,
      client_name: selectedClient!.display_name,
      folder_id: folder.id,
      folder_reference: folder.folder_reference,
      source_id: selectedClient!.source.id,
      match_status: folder.match_status as 'unique' | 'ambiguous',
      provisional: true,
    }))
  // The explicit Record context is a current, server-validated match. Keep
  // those exact details if the separately loaded catalogue is older/unavailable.
  const candidates = hints.map(
    (hint) =>
      current.find(
        (value) => value.client_id === hint.client_id && value.folder_id === hint.folder_id
      ) ?? hint
  )
  for (const hint of current)
    if (
      !candidates.some(
        (value) => value.client_id === hint.client_id && value.folder_id === hint.folder_id
      )
    )
      candidates.push(hint)
  const byClient = new Map<string, FundClientHint[]>()
  for (const hint of candidates) {
    const existing = byClient.get(hint.client_id) ?? []
    if (!existing.some((item) => JSON.stringify(item) === JSON.stringify(hint))) existing.push(hint)
    byClient.set(hint.client_id, existing)
  }
  return [...byClient].map(([id, clientHints]) => {
    const names = [...new Set(clientHints.map((hint) => hint.client_name).filter(Boolean))]
    return { id, name: names.length === 1 ? names[0]! : null, hints: clientHints }
  })
}

/** A source door needs the current client, source and exact fund-folder match. */
export function matchedFundFolders(fundId: string, context: FundClientContext, client: ClientView) {
  if (client.id !== context.id) return []
  return client.fund_folders.filter(
    (folder) =>
      folder.match_status !== 'unmatched' &&
      folder.matching_registered_funds.some((fund) => fund.id === fundId) &&
      context.hints.some(
        (hint) =>
          hint.client_id === client.id &&
          hint.source_id === client.source.id &&
          hint.folder_id === folder.id &&
          hint.folder_reference === folder.folder_reference
      )
  )
}
